import { useState } from 'react';
import type { ResenaModerada } from '@medchoice/shared';
import { administracion } from '../../api/administracion';
import { ApiError } from '../../api/client';
import { usePedido } from '../../api/usePedido';
import { Boton, Cargando, EstadoVacio, Estrellas, FalloDeCarga, Pestanas, Tarjeta, useAvisos } from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { fechaCorta } from '../../ui/format';

type Pestana = 'publicadas' | 'ocultas';

export function Resenas() {
  const pedido = usePedido(() => administracion.resenas(), []);
  const [pestana, setPestana] = useState<Pestana>('publicadas');
  const todas = pedido.datos ?? [];
  const publicadas = todas.filter((r) => r.visible);
  const ocultas = todas.filter((r) => !r.visible);

  return (
    <SeccionPanel titulo="Reseñas" descripcion="Las últimas reseñas publicadas. Ocultar una la saca del perfil y del puntaje">
      {pedido.estado === 'cargando' && <Cargando />}
      {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
      {pedido.datos && (
        <Tarjeta>
          <Pestanas<Pestana> etiqueta="Reseñas" activa={pestana} alElegir={setPestana} pestanas={[
            { id: 'publicadas', texto: 'Publicadas', cantidad: publicadas.length },
            { id: 'ocultas', texto: 'Ocultas', cantidad: ocultas.length },
          ]} />
          <div className="listado">
            {(pestana === 'publicadas' ? publicadas : ocultas).length === 0
              ? <EstadoVacio icono="star" titulo={pestana === 'publicadas' ? 'No hay reseñas publicadas' : 'No hay reseñas ocultas'} />
              : (pestana === 'publicadas' ? publicadas : ocultas).map((r) => (
                <FilaResena key={r.id_resena} resena={r} alCambiar={pedido.recargar} />
              ))}
          </div>
        </Tarjeta>
      )}
    </SeccionPanel>
  );
}

function FilaResena({ resena: r, alCambiar }: { resena: ResenaModerada; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [ocupado, setOcupado] = useState(false);
  const cambiar = async () => {
    setOcupado(true);
    try {
      await administracion.moderarResena(r.id_resena, !r.visible);
      mostrarAviso({ tono: 'exito', titulo: r.visible ? 'Reseña oculta' : 'Reseña publicada de nuevo' });
      alCambiar();
    } catch (e) {
      mostrarAviso({ tono: 'error', titulo: 'No se pudo cambiar la reseña', cuerpo: e instanceof ApiError ? e.message : undefined });
    } finally {
      setOcupado(false);
    }
  };
  return (
    <div className="resena-moderada">
      <div>
        <div className="resena-moderada__cabecera">
          <Estrellas puntaje={r.calificacion} tamano={14} />
          <span className="nota">{r.paciente} sobre {r.profesional} · {fechaCorta(r.fecha.slice(0, 10))}</span>
        </div>
        {r.comentario && <p className="texto-resena">{r.comentario}</p>}
      </div>
      <Boton tamano="chico" variante={r.visible ? 'fantasma' : 'secundario'} cargando={ocupado} onClick={() => void cambiar()}>
        {r.visible ? 'Ocultar' : 'Publicar de nuevo'}
      </Boton>
    </div>
  );
}
